import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import BookingModal from "@/components/BookingModal";
import InstagramSticky from "@/components/InstagramSticky";
import { getDiary, DIARIES } from "@/lib/diaries";
import { CameraIcon, CherrySwirl, PolaroidIcon, WaveDivider, Spark } from "@/components/Brand";

function Reveal({ children, delay = 0, className = "" }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => e.isIntersecting && setVisible(true),
      { threshold: 0.12, rootMargin: "0px 0px -8% 0px" }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return (
    <div
      ref={ref}
      className={`transition-all duration-[1100ms] ease-[cubic-bezier(0.16,1,0.3,1)] ${
        visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
      } ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
}

export default function DiarioDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const diary = getDiary(slug);
  const [bookingOpen, setBookingOpen] = useState(false);

  useEffect(() => {
    if (!diary) navigate("/diario", { replace: true });
    window.scrollTo({ top: 0 });
  }, [diary, navigate]);

  if (!diary) return null;

  const others = DIARIES.filter((d) => d.slug !== diary.slug);

  return (
    <main className="bg-cerenza-cream text-cerenza-ink overflow-x-clip" data-testid="diario-detail">
      <Nav onBook={() => setBookingOpen(true)} />

      {/* HERO */}
      <section className="relative pt-32 md:pt-40 pb-12 md:pb-16">
        <CherrySwirl color="#cd2e2e" className="hidden md:block absolute top-32 right-[6%] w-14 h-14 opacity-50 animate-float" />
        <div className="max-w-[1320px] mx-auto px-6 md:px-12 text-center">
          <Link to="/diario" data-testid="back-to-diary" className="eyebrow inline-flex items-center gap-2 hover:opacity-70 transition-opacity">
            <span className="block h-px w-6 bg-current" /> Diario visual
          </Link>
          <h1 className="font-display text-5xl md:text-[80px] leading-[0.95] text-cerenza-cherry mt-5 max-w-4xl mx-auto text-balance">
            {diary.title}
          </h1>
          <p className="mt-5 text-cerenza-ink/65 text-sm uppercase tracking-[0.22em] font-semibold">
            {diary.subtitle} · {diary.date}
          </p>
        </div>
      </section>

      {/* HERO IMAGE */}
      <Reveal className="max-w-[1320px] mx-auto px-6 md:px-12">
        <div className={`relative overflow-hidden rounded-[60px_24px_60px_24px] shadow-[0_30px_60px_-24px_rgba(220,45,63,0.35)] ${diary.fit === "contain" ? (diary.monochrome ? "bg-cerenza-pinklight" : "bg-cerenza-cream") : ""}`}>
          <img src={diary.heroImage} alt={diary.title} className={`w-full h-[60vh] md:h-[75vh] ${diary.fit === "contain" ? "object-contain" : "object-cover object-top"} ${diary.monochrome ? "" : "photo-warm"}`} />
        </div>
      </Reveal>

      {/* INTRO TEXT */}
      <section className="relative">
        <PolaroidIcon color="#cd2e2e" className="hidden md:block absolute top-20 left-[6%] w-12 h-12 opacity-40 animate-float" />
        <div className="max-w-[820px] mx-auto px-6 md:px-12 py-20 md:py-28">
          <Reveal>
            <p className="font-script text-3xl md:text-4xl text-cerenza-cherry">— el comienzo</p>
            <p className="mt-6 font-display text-[28px] md:text-[36px] leading-[1.25] text-cerenza-ink text-pretty">
              {diary.intro}
            </p>
          </Reveal>
        </div>
      </section>

      <WaveDivider from="#ffffd7" to="#f9d4d3" />

      {/* DIARY SECTIONS */}
      <section className="bg-cerenza-pinklight">
        <div className="max-w-[1320px] mx-auto px-6 md:px-12 py-20 md:py-28 space-y-24 md:space-y-32">
          {diary.sections.map((s, idx) => {
            // Stable key derived from content so React reconciles correctly
            // even if sections are reordered/added in diaries.js.
            const sKey = `${s.kind}-${s.image || s.images?.[0] || s.body?.slice(0, 24) || idx}`;
            if (s.kind === "text") {
              return (
                <Reveal key={sKey}>
                  <div className="max-w-[760px] mx-auto py-6 md:py-10">
                    <p className="font-display text-2xl md:text-[32px] leading-[1.35] text-cerenza-ink text-pretty">
                      {s.body}
                    </p>
                  </div>
                </Reveal>
              );
            }
            if (s.kind === "single") {
              const aspect =
                s.layout === "tall" ? "aspect-[3/4]"
                : s.layout === "wide" ? "aspect-[3/2]"
                : s.layout === "square" ? "aspect-square"
                : s.layout === "portrait" ? "aspect-[4/5]"
                : "aspect-[4/5]";
              const colSpan =
                s.layout === "wide" ? "md:col-span-12"
                : s.layout === "tall" ? "md:col-span-6 md:col-start-4"
                : "md:col-span-8 md:col-start-3";
              const focalCls = s.focal === "top" ? "object-top" : s.focal === "bottom" ? "object-bottom" : "object-center";
              const tone = diary.monochrome ? "" : "photo-warm";
              const fitContain = s.fit === "contain" || diary.fit === "contain";
              const fitCls = fitContain ? "object-contain" : "object-cover";
              const bgCls = fitContain ? (diary.monochrome ? "bg-cerenza-pinklight" : "bg-cerenza-cream") : "";
              return (
                <Reveal key={sKey} className="grid grid-cols-12 gap-6">
                  <figure className={`col-span-12 ${colSpan}`}>
                    <div className={`overflow-hidden rounded-[40px] ${aspect} ${bgCls} shadow-[0_24px_50px_-22px_rgba(220,45,63,0.35)]`}>
                      <img src={s.image} alt={s.caption || ""} className={`w-full h-full ${fitCls} ${focalCls} ${tone}`} />
                    </div>
                    {s.caption && (
                      <figcaption className="mt-5 font-script text-xl md:text-2xl text-cerenza-cherry">
                        — {s.caption}
                      </figcaption>
                    )}
                  </figure>
                </Reveal>
              );
            }
            if (s.kind === "duo") {
              const tone = diary.monochrome ? "" : "photo-warm";
              const fitContain = s.fit === "contain" || diary.fit === "contain";
              const fitCls = fitContain ? "object-contain" : "object-cover";
              const bgCls = fitContain ? (diary.monochrome ? "bg-cerenza-pinklight" : "bg-cerenza-cream") : "";
              return (
                <Reveal key={sKey} className="grid grid-cols-12 gap-6 md:gap-8">
                  {s.images.map((img, i) => {
                    const focalCls = s.focals?.[i] === "top" ? "object-top" : s.focals?.[i] === "bottom" ? "object-bottom" : "object-center";
                    return (
                      <figure key={img} className={`col-span-12 md:col-span-6 ${i === 0 ? "md:mt-12" : ""}`}>
                        <div className={`overflow-hidden rounded-[40px] aspect-[4/5] ${bgCls} shadow-[0_24px_50px_-22px_rgba(220,45,63,0.35)]`}>
                          <img src={img} alt={s.captions?.[i] || ""} className={`w-full h-full ${fitCls} ${focalCls} ${tone}`} />
                        </div>
                        {s.captions?.[i] && (
                          <figcaption className="mt-5 font-script text-lg md:text-xl text-cerenza-cherry">
                            — {s.captions[i]}
                          </figcaption>
                        )}
                      </figure>
                    );
                  })}
                </Reveal>
              );
            }
            return null;
          })}
        </div>
      </section>

      {/* CLOSING TEXT */}
      {diary.closing && (
        <section className="bg-cerenza-cream py-20 md:py-28">
          <div className="max-w-[820px] mx-auto px-6 md:px-12 text-center">
            <Reveal>
              <p className="font-script text-3xl md:text-4xl text-cerenza-cherry">— el cierre</p>
              <p className="mt-6 font-display text-[26px] md:text-[34px] leading-[1.3] text-cerenza-ink text-pretty">
                {diary.closing}
              </p>
            </Reveal>
          </div>
        </section>
      )}

      {/* QUOTE */}
      {diary.quote && (
        <section className="bg-cerenza-cream py-24 md:py-32 relative">
          <CameraIcon color="#cd2e2e" className="hidden md:block absolute top-16 right-[10%] w-12 h-12 opacity-30 animate-float" />
          <div className="max-w-[920px] mx-auto px-6 md:px-12 text-center">
            <Reveal>
              <Spark color="#cd2e2e" className="w-8 h-8 mx-auto" />
              <blockquote className="mt-6 font-display text-3xl md:text-5xl text-cerenza-ink leading-[1.15] text-pretty">
                “{diary.quote}”
              </blockquote>
              <figcaption className="mt-8 text-sm uppercase tracking-[0.22em] font-semibold text-cerenza-cherry">
                — {diary.quoteAuthor}
              </figcaption>
            </Reveal>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="bg-cerenza-cherry text-cerenza-creamlight relative overflow-hidden">
        <CherrySwirl color="#ffffd7" className="absolute top-10 left-[10%] w-14 h-14 opacity-25" />
        <PolaroidIcon color="#ffffd7" className="absolute bottom-10 right-[12%] w-14 h-14 opacity-25" />
        <div className="max-w-[1100px] mx-auto px-6 md:px-12 py-20 md:py-24 text-center">
          <p className="font-script text-3xl text-cerenza-pink">— el siguiente capítulo</p>
          <h2 className="font-display text-5xl md:text-6xl mt-3 leading-[1.05] text-balance">
            Tu historia también puede vivir aquí.
          </h2>
          <button
            data-testid="diary-cta"
            onClick={() => setBookingOpen(true)}
            className="mt-10 inline-flex items-center gap-3 px-9 py-4 bg-cerenza-cream text-cerenza-cherry rounded-full font-semibold uppercase tracking-[0.14em] text-xs hover:bg-cerenza-pink transition-all duration-300 hover:-translate-y-0.5"
          >
            Reserva tu experiencia →
          </button>
        </div>
      </section>

      {/* OTHER DIARIES */}
      {others.length > 0 && (
        <section className="bg-cerenza-cream py-20 md:py-28">
          <div className="max-w-[1320px] mx-auto px-6 md:px-12">
            <p className="eyebrow">— Otras historias</p>
            <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-8">
              {others.slice(0, 3).map((d) => (
                <Link key={d.slug} to={`/diario/${d.slug}`} className="group block">
                  <div className="aspect-[4/5] overflow-hidden rounded-[32px]">
                    <img src={d.coverImage} alt={d.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105 photo-warm" />
                  </div>
                  <h4 className="mt-4 font-display text-xl text-cerenza-ink group-hover:text-cerenza-cherry transition-colors">{d.title}</h4>
                  <p className="text-sm text-cerenza-ink/60">{d.date}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <Footer onBook={() => setBookingOpen(true)} />
      <BookingModal open={bookingOpen} onOpenChange={setBookingOpen} />
      <InstagramSticky />
    </main>
  );
}
